import { convertKeysToCamelCase } from '@/utils/convertKeysToCamelCase/convertKeysToCamelCase';
import { commaFormat } from '@/utils/index';
import { pluralizeString } from '@/utils/pluralizeString';
import { FetchTrackedVehicleResponse } from '../../../../myGarage/oramInterfaces';

interface ServiceDeadline {
  dueDate: string;
  timeDiff: {
    month: number;
    day: number;
    diff: string;
  };
  percent: number;
  alertText?: string;
  alertType?: string;
}

interface MileageReport {
  mileage: number;
  source: string;
  createdAt: string;
}

interface ValuationReport {
  value: number | null;
  source: string | null;
  createdAt: string | null;
}

interface Registration {
  expirationDate: string | null;
  source: string | null;
  createdAt: string | null;
  serviceDeadline: ServiceDeadline | null;
  dueText: string | null;
}

interface AverageDrivenMileage {
  averageMileage: number | null;
  source: string | null;
  createdAt: string | null;
}

interface Location {
  city: string | null;
  state: string | null;
  zip: string | null;
  source: string | null;
  createdAt: string | null;
}

interface ServiceItem {
  id: number;
  serviceType: string;
  lastChange: number;
  suggestedChange: number;
  completed: boolean;
  completedMileage: number | null;
  source: string;
  serviceDueMileage: number;
  remainingMileageTillService: number;
  percentOfServiceLifeRemaining: number;
  createdAt: string;
}

interface ParsedServiceItem extends ServiceItem {
  percent: number;
  nextServiceMileage: number;
  milesRemaining: number | null;
  noReportedMileage: boolean;
  milesRemainingText: string;
  alertText: string;
  alertType: string;
  serviceNeeded: boolean;
  serviceDueMileageParsed: string;
}

export interface TrackedVehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  trim?: string | null;
  vin?: string | null;
  mileage?: number;
  registration: Registration;
  averageDrivenMileage: AverageDrivenMileage;
  location: Location;
  valuationReport: ValuationReport;
  mileageReport: MileageReport;
  serviceItems: ServiceItem[];
  licensePlate: string | null;
  state: string | null;
  lastUpdatedAt: string | null;
  currentlyTracked: boolean;
}

export interface ParsedTrackedVehicle extends TrackedVehicle {
  queryKey: string;
  serviceItems: ParsedServiceItem[];
}

export const parseTrackedVehicleResponse = (
  response: FetchTrackedVehicleResponse,
  queryKey: string
) => {
  const basicFormattedResponse = convertKeysToCamelCase(response);
  const { vehicle } = basicFormattedResponse;
  vehicle.registration = parseRegistration(vehicle.registration);
  vehicle.serviceItems = vehicle.serviceItems.map(
    (serviceItem: ServiceItem) => {
      return parseServiceItem(serviceItem);
    }
  );
  vehicle.queryKey = queryKey;
  return vehicle as ParsedTrackedVehicle;
};

export const parseRegistration = (registration: Registration) => {
  const registrationServiceDeadline = registration.serviceDeadline;
  if (!registrationServiceDeadline) return;
  const monthsLeft = registrationServiceDeadline.timeDiff.month;
  const daysLeft = registrationServiceDeadline.timeDiff.day;

  const dueText = `Due ${registrationServiceDeadline.dueDate}`;

  // If the vehicle has 12 or more months left, this allows the progress bar to still display some of the bar
  if (monthsLeft >= 12) {
    registrationServiceDeadline.percent = 2;
  }
  if (monthsLeft <= 1) {
    registrationServiceDeadline.percent = 90;
    registrationServiceDeadline.alertText = 'Renewal needed';
    registrationServiceDeadline.alertType = 'error';
    if (daysLeft <= 10) {
      registrationServiceDeadline.percent = 100;
    }
  } else if (monthsLeft <= 3) {
    registrationServiceDeadline.percent = 80;
    registrationServiceDeadline.alertText = 'Renew Soon!';
    registrationServiceDeadline.alertType = 'active';
  }
  return {
    ...registration,
    serviceDeadline: registrationServiceDeadline,
    dueText,
  };
};

export const parseServiceItem = (serviceItem: ServiceItem) => {
  const minimumDisplayPercentage = 2;

  const obj: ParsedServiceItem = {
    ...serviceItem,
    percent: serviceItem.percentOfServiceLifeRemaining,
    nextServiceMileage: serviceItem.serviceDueMileage,
    milesRemaining: serviceItem.remainingMileageTillService,
    noReportedMileage: false,
    milesRemainingText: '',
    alertText: '',
    alertType: '',
    serviceNeeded: false,
    serviceDueMileageParsed: '',
  };

  // We always want to display at least the minimumDisplayPercentage width for the progress bar.
  if (obj.percent < minimumDisplayPercentage) {
    obj.percent = minimumDisplayPercentage;
  }

  if (obj.percent > 100) {
    obj.percent = 100;
  }

  if (typeof obj.milesRemaining !== 'number') {
    obj.noReportedMileage = true;
    return obj;
  }

  obj.serviceDueMileageParsed = `${commaFormat(
    obj.nextServiceMileage
  )} ${pluralizeString('mile', obj.nextServiceMileage)}`;

  if (typeof obj.milesRemaining === 'number' && obj.milesRemaining > 0) {
    obj.milesRemainingText = `${commaFormat(obj.milesRemaining)} miles left`;
  }

  if (obj.milesRemaining <= 1000) {
    obj.alertText = 'Needed soon!';
    obj.alertType = 'active';
  }
  if (obj.milesRemaining <= 500) {
    obj.alertText = 'Service needed';
    obj.alertType = 'error';
    obj.serviceNeeded = true;
    obj.milesRemainingText = `${
      serviceItem.serviceType
    } needed at ${commaFormat(obj.nextServiceMileage)} miles`;
  }
  return obj;
};
